import { IconFont } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import cls from 'classnames'
import type { CSSProperties } from 'react'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { useRecordData } from './useRecordData'

interface RelationNormalTagProps {
    recordId: string
    source: 'input' | 'tag' | 'checkbox'
    dsId: string
    relativeField: Field | undefined
    relativeShowField: Field | undefined
    style?: CSSProperties
    enableDelete?: boolean
    prefix?: React.ReactNode
    isActive?: boolean
    disabled?: boolean
    onRemove?: () => void
    onClick?: React.MouseEventHandler<HTMLSpanElement>
}
const SCxClearIconWrapper = styled.div``
const RelationItemWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    flex-wrap: nowrap;
    height: 28px;
    min-width: 28px;
    padding: 0 6px;
    gap: 4px;
    color: var(--color-black);
    text-align: center;
    overflow: hidden;

    &.disabled {
        cursor: not-allowed;
    }
`

const RelationName = styled.span`
    font-size: var(--font-size-normal);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

export const RelationNormalTag: React.FC<RelationNormalTagProps> = ({
    recordId,
    source,
    dsId,
    relativeField,
    relativeShowField,
    enableDelete = true,
    style,
    disabled: propDisabled,
    isActive,
    onClick,
    onRemove
}) => {
    const {
        record,
        displayValue,
        fieldRawValue,
        disabled: recordDisabled
    } = useRecordData(recordId, dsId, relativeField, relativeShowField)

    const disabled = propDisabled || recordDisabled
    const elDataAttr = useMemo(() => {
        if (source === 'input') {
            return {
                wrapper: {
                    'data-field-other': true,
                    'data-field-disabled': !!disabled
                },
                icon: {
                    'data-field-other-icon': true
                }
            }
        }
        return {
            wrapper: {
                'data-field-input': true,
                'data-field-active': isActive,
                'data-field-disabled': !!disabled
            },
            icon: {
                'data-field-icon': true
            }
        }
    }, [disabled, isActive, source])

    if (!record) {
        return null
    }

    return (
        <RelationItemWrapper
            {...elDataAttr.wrapper}
            onClick={ev => {
                if (disabled) {
                    return
                }
                onClick?.(ev)
            }}
            style={style}
            className={cls({ active: isActive, disabled })}
        >
            <RelationName>{displayValue ?? '未命名'}</RelationName>
            {enableDelete && (
                <SCxClearIconWrapper
                    {...elDataAttr.icon}
                    onClick={ev => {
                        ev.stopPropagation()
                        onRemove?.()
                    }}
                >
                    <IconFont style={{ cursor: 'pointer' }} size={12} type="Close" />
                </SCxClearIconWrapper>
            )}
        </RelationItemWrapper>
    )
}
